import { useState } from "react";
import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { RoleComponentGuard } from "components/guards";
import { useEntityTagActions } from "store";
import { IEntityTag } from "store/actions/deal-actions/tag-entity-actions";
import { IApiServiceError } from "store/services";

import { RoleName } from "../../../../constants";

const tagCountChipSx = {
  width: 24,
  minWidth: 24,
  height: 24,
  fontSize: 12,
  fontWeight: 500,
  padding: 0,
  backgroundColor: "#e8eef4",
  color: "#4a5f7f",
  borderRadius: "50%",
  "& .MuiChip-label": { padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }
};

const tagChipSx = {
  padding: "0 5px",
  height: 24,
  backgroundColor: "#DBE8F5",
  color: "#071f35",
  borderRadius: "20px",
  "& .MuiChip-label": { px: "4px", py: 0, fontSize: 12, lineHeight: "16px" }
};

interface TagsCellProps {
  tags: string[];
  entityType: "section" | "document" | "form" | "file";
  entityId: number;
  disabled?: boolean;
  onTagsUpdated?: (entityId: number, entityType: string, tags: IEntityTag[]) => void;
}

type TagOption = IEntityTag | string;

const getTagLabel = (tag: TagOption): string => (typeof tag === "string" ? tag : tag.name);

export const TagsCell = ({ tags, entityType, entityId, disabled = false, onTagsUpdated }: TagsCellProps) => {
  const { getEntityTagsAction, syncEntityTagsAction } = useEntityTagActions();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [allTags, setAllTags] = useState<IEntityTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTags, setEditTags] = useState<TagOption[]>([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setIsEditing(false);
  };

  const handleStartEdit = async () => {
    setIsEditing(true);
    setNewTagInput("");
    setError(null);
    setLoadingTags(true);
    try {
      const fetched = await getEntityTagsAction();
      setAllTags(fetched);
      const matched: TagOption[] = tags.map((name) => fetched.find((t) => t.name === name) ?? name);
      setEditTags(matched);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsEditing(false);
  };

  const handleAddTag = (valueToAdd?: TagOption) => {
    const tag = valueToAdd ?? newTagInput.trim();
    const trimmed = typeof tag === "string" ? tag.trim() : tag;
    if (!trimmed || (typeof trimmed === "string" && trimmed === "")) return;
    const alreadyAdded = editTags.some((t) => getTagLabel(t).toLowerCase() === getTagLabel(trimmed).toLowerCase());
    if (alreadyAdded) {
      setNewTagInput("");
      return;
    }
    const resolved =
      typeof trimmed === "string"
        ? (allTags.find((t) => t.name.toLowerCase() === trimmed.toLowerCase()) ?? trimmed)
        : trimmed;
    setEditTags((prev) => [...prev, resolved]);
    setNewTagInput("");
    setError(null);
  };

  const handleRemoveTag = (index: number) => {
    setEditTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApply = async () => {
    const tag_ids = editTags.filter((t): t is IEntityTag => typeof t !== "string").map((t) => t.id);
    const tag_names = editTags.filter((t): t is string => typeof t === "string");
    setSaving(true);
    setError(null);
    try {
      const updatedTags = await syncEntityTagsAction({ entity_type: entityType, entity_id: entityId, tag_ids, tag_names });
      onTagsUpdated?.(entityId, entityType, updatedTags);
      handleClose();
    } catch (err) {
      const apiErr = err as IApiServiceError;
      if (apiErr?.errors && Object.keys(apiErr.errors).length > 0) {
        const messages: string[] = [];
        for (const [key, msgs] of Object.entries(apiErr.errors)) {
          const match = key.match(/^tag_names\.(\d+)$/);
          if (match) {
            const idx = parseInt(match[1], 10);
            const tagName = tag_names[idx];
            messages.push(tagName ? `Tag "${tagName}" already exists.` : msgs[0]);
          }
        }
        if (messages.length > 0) {
          setError(messages.join(" "));
          return;
        }
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Chip
        label={tags.length === 0 ? "No tags" : tags.length === 1 ? tags[0] : tags.length}
        size="small"
        onClick={disabled ? undefined : handleOpen}
        sx={{
          cursor: disabled ? "default" : "pointer",
          ...(tags.length === 0 && {
            ...tagCountChipSx,
            width: "auto",
            borderRadius: "12px",
            px: "8px",
            color: "#92a7bc",
            backgroundColor: "#f0f4f8"
          }),
          ...(tags.length === 1 && {
            ...tagChipSx
          }),
          ...(tags.length > 1 && {
            ...tagCountChipSx
          })
        }}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={async () => {
          if (isEditing) {
            await handleApply();
          } else {
            handleClose();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            position: "relative",
            maxWidth: "450px",
            borderRadius: "8px",
            boxShadow:
              "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
            p: "24px"
          }
        }}
      >
        <AsyncContentMui isLoading={saving} boxProps={{ sx: { minWidth: 100, minHeight: 40 } }}>
          {!isEditing ? (
            <Stack direction="row" flexWrap="wrap" gap="8px" alignItems="center" sx={{ width: "100%" }}>
              {tags.length > 0 ? (
                tags.map((tag, index) => <Chip key={index} label={tag} size="small" sx={tagChipSx} />)
              ) : (
                <Typography sx={{ fontSize: 12, color: "#92a7bc" }}>No tags</Typography>
              )}
              <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                <IconButton
                  onClick={handleStartEdit}
                  size="sm"
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "32px",
                    backgroundColor: "rgba(255,255,255,0.96)",
                    "&:hover": { backgroundColor: "rgba(7,31,53,0.04)" }
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16, color: "#071f35" }} />
                </IconButton>
              </RoleComponentGuard>
            </Stack>
          ) : (
            <Stack gap="20px" sx={{ width: "400px" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 400, lineHeight: "24px", color: "#071f35", pb: "8px" }}>
                Tags
              </Typography>

              <Stack gap="10px">
                <Stack direction="row" gap="10px" alignItems="flex-start">
                  <Autocomplete
                    freeSolo
                    options={allTags.filter(
                      (t) => !editTags.some((et) => getTagLabel(et).toLowerCase() === t.name.toLowerCase())
                    )}
                    getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                    inputValue={newTagInput}
                    onInputChange={(_, value, reason) => {
                      if (reason !== "reset") setNewTagInput(value);
                    }}
                    onChange={(_, selected) => {
                      if (selected) {
                        handleAddTag(selected as TagOption);
                      }
                    }}
                    loading={loadingTags}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="New Tag"
                        placeholder="Add Tag"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: loadingTags ? <CircularProgress size={14} /> : null
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: 14,
                            lineHeight: "16px",
                            height: 32,
                            "& fieldset": { borderColor: "#7b92a9" },
                            "&:hover fieldset": { borderColor: "#7b92a9" },
                            "&.Mui-focused fieldset": { borderColor: "#7b92a9" }
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#496682",
                            "&.Mui-focused": { color: "#496682" }
                          },
                          "& input::placeholder": { color: "#92a7bc", opacity: 1 },
                          "& .MuiInputBase-input": { py: "8px", px: "8px" }
                        }}
                      />
                    )}
                  />
                  <Button
                    onClick={() => handleAddTag()}
                    disabled={!newTagInput.trim()}
                    sx={{
                      borderRadius: "32px",
                      px: "16px",
                      py: "4px",
                      fontSize: 16,
                      lineHeight: "24px",
                      textTransform: "none",
                      color: newTagInput.trim() ? "#071f35" : "#8d98a2",
                      boxShadow: "none",
                      border: "none",
                      minWidth: "unset",
                      whiteSpace: "nowrap",
                      backgroundColor: "#f5f6f7",
                      "&:hover": { backgroundColor: "rgba(7,31,53,0.04)", boxShadow: "none" },
                      "&.Mui-disabled": { color: "#8d98a2", backgroundColor: "rgba(255,255,255,0.96)" }
                    }}
                  >
                    Add
                  </Button>
                </Stack>

                <AsyncContentMui
                  isLoading={loadingTags}
                  spinnerProps={{ size: 16 }}
                  boxProps={{ sx: { justifyContent: "flex-start", minHeight: 24 } }}
                >
                  {editTags.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" gap="8px">
                      {editTags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={getTagLabel(tag)}
                          size="small"
                          deleteIcon={<CloseIcon />}
                          onDelete={() => handleRemoveTag(index)}
                          sx={{
                            ...tagChipSx,
                            "& .MuiChip-deleteIcon": {
                              fontSize: "11px",
                              color: "#071f35",
                              margin: 0,
                              width: 16,
                              height: 16,
                              padding: "2.5px",
                              flexShrink: 0,
                              "&:hover": { color: "#071f35", opacity: 0.7 }
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </AsyncContentMui>
              </Stack>

              {error && <Typography sx={{ fontSize: 12, color: "#d32f2f" }}>{error}</Typography>}
            </Stack>
          )}
        </AsyncContentMui>
      </Popover>
    </>
  );
};
